import { LogIn } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api.ts';
import { useAuth } from '../lib/auth.tsx';

/** Codes renvoyés par le flux Google, traduits ici.
 *
 *  Le serveur redirige avec un code plutôt qu'un message : le texte appartient
 *  à l'interface, et un message d'erreur trimballé dans une URL est un vecteur
 *  d'injection tout trouvé. */
const OAUTH_ERRORS: Record<string, string> = {
    annule: 'Connexion Google annulée.',
    google_non_configure: "La connexion Google n'est pas configurée sur ce serveur.",
    google_desactive: 'La connexion Google est désactivée.',
    session_expiree: 'La tentative a expiré. Réessayez.',
    state_invalide: 'Requête de connexion invalide. Réessayez depuis cette page.',
    echange_impossible: "Google n'a pas confirmé la connexion. Réessayez.",
    compte_desactive: 'Ce compte est désactivé. Contactez un administrateur.',
    email_non_verifie: "Cette adresse Google n'est pas vérifiée.",
    demande_refusee: "Votre demande d'accès a été refusée.",
    inscription_fermee: "L'inscription est fermée sur ce serveur.",
    reponse_invalide: 'Réponse inattendue de Google. Réessayez.',
};

/** Ce qui n'est pas un échec. Un message d'attente affiché en rouge se lit
 *  comme un refus — il a sa propre couleur et son propre paramètre d'URL. */
const OAUTH_NOTICES: Record<string, string> = {
    en_attente_validation:
        'Votre demande a bien été transmise. Un administrateur doit la valider ; '
        + 'vous pourrez vous connecter dès qu\'elle sera acceptée.',
};

interface Providers { google: boolean; password: boolean }

export function Login() {
    const { user, loading, login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [providers, setProviders] = useState<Providers>({ google: false, password: true });

    /* Quels moyens de connexion ce serveur propose.
     *
     * On RÉESSAIE, et c'est le point délicat : en cas d'échec le bouton Google
     * disparaît purement et simplement, sans que rien ne l'explique — or un
     * redémarrage du serveur, une limite de débit atteinte ou un réseau qui
     * tousse au bord d'un fairway sont tous transitoires. Une seule requête
     * ratée ne doit pas retirer une porte d'entrée pour la durée de la visite.
     *
     * Les délais montent jusqu'à la demi-minute : une limite de débit se purge
     * en une minute, trois essais collés n'y survivraient pas.
     *
     * Le défaut reste `google: false`. Afficher le bouton sans savoir mènerait
     * sur une page d'erreur si le serveur n'a pas Google configuré ; le
     * formulaire mot de passe, lui, marche toujours. */
    useEffect(() => {
        const attentes = [0, 2_000, 8_000, 20_000];
        let vivant = true;
        const minuteries: ReturnType<typeof setTimeout>[] = [];

        const tenter = (essai: number): void => {
            void api<Providers>('/auth/providers')
                .then((p) => { if (vivant) setProviders(p); })
                .catch(() => {
                    if (!vivant || essai + 1 >= attentes.length) return;
                    minuteries.push(setTimeout(() => tenter(essai + 1), attentes[essai + 1]!));
                });
        };
        tenter(0);

        return () => { vivant = false; minuteries.forEach(clearTimeout); };
    }, []);

    const oauthError = searchParams.get('error');
    const oauthInfo = searchParams.get('info');
    const [notice, setNotice] = useState<string | null>(null);

    useEffect(() => {
        if (oauthError) setError(OAUTH_ERRORS[oauthError] ?? 'Connexion Google impossible.');
    }, [oauthError]);
    useEffect(() => {
        if (oauthInfo) setNotice(OAUTH_NOTICES[oauthInfo] ?? null);
    }, [oauthInfo]);

    if (!loading && user) {
        const from = (location.state as { from?: string } | null)?.from;
        return <Navigate to={from && from !== '/connexion' ? from : '/'} replace />;
    }

    const submit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
            await login(email, password);
            void navigate('/');
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Connexion impossible');
        } finally {
            setBusy(false);
        }
    };

    return (
        /* Le seul écran où le fond fairway prend toute la page.
         *
         * C'est le seul moment de l'application où personne ne regarde son
         * téléphone en marchant : on s'arrête, on se connecte, on part
         * ensuite. La carte du formulaire reste blanche — chaque champ, chaque
         * alerte y garde le contraste éprouvé du thème « Plein soleil »,
         * plutôt que de réinventer des couleurs de saisie sur fond sombre. */
        <div className="fairway-texture flex min-h-dvh items-center justify-center px-4 py-10">
            <div className="w-full max-w-sm">
                <div className="mb-6 text-center">
                    <div className="mx-auto flex size-14 items-center justify-center rounded-2xl bg-white/12 text-3xl" aria-hidden="true">⛳</div>
                    <h1 className="font-display mt-4 text-4xl font-bold tracking-tight text-white">Golf</h1>
                    <p className="mt-1.5 text-sm font-semibold text-white/60">golf.fevio.fr</p>
                </div>

                {/* `text-base-content` referme le blanc hérité de
                    `.fairway-texture` : sans lui, les libellés de champ et le
                    « ou » du séparateur restaient blancs sur cette carte
                    claire — blanc sur blanc, invisibles. */}
                <div className="rounded-box bg-base-100 p-5 text-base-content shadow-2xl">
                    {notice && (
                        <div role="status" className="alert alert-info mb-4 text-sm">
                            <span>{notice}</span>
                        </div>
                    )}

                    {/* Google d'abord : c'est le chemin qu'on veut voir emprunté,
                        et celui qui évite de retaper des identifiants. Un lien et
                        non un bouton fetch — le flux OAuth est une navigation. */}
                    {providers.google && (
                        <a href="/api/auth/google" className="btn btn-block h-14 gap-3 bg-white text-black hover:bg-white/90">
                            <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
                                <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.6l6.8-6.8C35.9 2.4 30.4 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.9 6.2C12.4 13.6 17.7 9.5 24 9.5z" />
                                <path fill="#4285F4" d="M46.1 24.6c0-1.6-.1-3.1-.4-4.6H24v9.1h12.4c-.5 2.9-2.2 5.4-4.6 7l7.2 5.6c4.2-3.9 6.6-9.7 6.6-16.6z" />
                                <path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6l-7.9-6.2C1 16.3 0 20 0 24s1 7.7 2.6 10.8l7.9-6.2z" />
                                <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.2-5.6c-2 1.4-4.6 2.2-8.7 2.2-6.3 0-11.6-4.1-13.5-9.9l-7.9 6.2C6.5 42.6 14.6 48 24 48z" />
                            </svg>
                            Continuer avec Google
                        </a>
                    )}

                    {providers.google && providers.password && (
                        <div className="my-5 flex items-center gap-3 text-xs opacity-50">
                            <span className="h-px flex-1 bg-base-content/20" />ou<span className="h-px flex-1 bg-base-content/20" />
                        </div>
                    )}

                    {!providers.password && !providers.google && (
                        <div role="alert" className="alert alert-warning text-sm">
                            <span>Aucun moyen de connexion n'est activé sur ce serveur.</span>
                        </div>
                    )}

                    <form
                        onSubmit={(e) => void submit(e)}
                        className={`space-y-4 ${providers.password ? '' : 'hidden'}`}
                    >
                        <label className="form-control w-full">
                            <span className="label-text mb-1 block text-sm">Adresse e-mail</span>
                            <input
                                type="email"
                                className="input input-bordered w-full"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="username"
                                required
                                autoFocus
                            />
                        </label>

                        <label className="form-control w-full">
                            <span className="label-text mb-1 block text-sm">Mot de passe</span>
                            <input
                                type="password"
                                className="input input-bordered w-full"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                            />
                        </label>

                        {error && (
                            <div role="alert" className="alert alert-error text-sm">
                                <span>{error}</span>
                            </div>
                        )}

                        <button type="submit" className="btn btn-accent btn-block tap-target" disabled={busy}>
                            {busy ? <span className="loading loading-spinner" /> : <LogIn size={20} />}
                            Se connecter
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
